/**
 * lm-io-server setup of a config (docs/wiring-documentation-proposal.md,
 * "3. Config content" and "Software").
 *
 * lm-io-server has no driver: nothing applies this. The config documents
 * which signals it captures (sources: "yg" on DL7) and plays out (outputs),
 * by port ID, and the UI shows each port's `device_index` so it can be
 * entered in lm-io-server by hand. The format stays plain so lm-io-server
 * could later pull it from Hub itself.
 *
 * Not bridge-owned: it stays editable while the config is active, and
 * bridge pushes keep the cloud's copy (`mergeCloudOwnedConfigData`).
 */
import { captureCardPortAddress } from "./device-templates.js";
export const LM_IO_ENTRY_KINDS = ["sources", "outputs"];
export const LM_IO_ENTRY_LABELS = {
    sources: "source",
    outputs: "output",
};
export function emptyLmIoServerSetup() {
    return { device_id: null, sources: [], outputs: [] };
}
/** The setup of config data, or an empty one. */
export function lmIoServerSetupOf(data) {
    const setup = data?.lm_io_server;
    return {
        device_id: setup?.device_id ?? null,
        sources: setup?.sources ?? [],
        outputs: setup?.outputs ?? [],
    };
}
/** Add or replace an entry (by ID). */
export function upsertLmIoServerEntry(setup, kind, entry) {
    const list = setup[kind];
    const index = list.findIndex((other) => other.id === entry.id);
    const clean = {
        id: entry.id,
        name: entry.name.trim(),
        port_id: entry.port_id || null,
        notes: entry.notes?.trim() || null,
    };
    return {
        ...setup,
        [kind]: index < 0
            ? [...list, clean]
            : list.map((other, i) => (i === index ? clean : other)),
    };
}
export function removeLmIoServerEntry(setup, kind, id) {
    return { ...setup, [kind]: setup[kind].filter((entry) => entry.id !== id) };
}
/**
 * The address to enter in lm-io-server for a port: its `device_index`
 * (position across all capture cards of the PC). Null for ports that are
 * not on a capture card inside a PC.
 */
export function lmIoDeviceIndex(portID, devices, ports) {
    if (!portID)
        return null;
    return captureCardPortAddress(portID, devices, ports)?.deviceIndex ?? null;
}
/** Ports lm-io-server can use: capture card ports, of its own PC if known. */
export function lmIoCandidatePorts(setup, devices, ports) {
    const server = setup.device_id
        ? devices.find((device) => device.id === setup.device_id)
        : null;
    return ports.filter((port) => {
        if (!captureCardPortAddress(port.id, devices, ports))
            return false;
        if (!server?.parentID)
            return true;
        const card = devices.find((device) => device.id === port.deviceID);
        return card?.parentID === server.parentID;
    });
}
